// 2026-09-27 US-010: Loyalty "10 + 1" dashboard (Screen 1). Lists every customer with at
// least one unit received by KFS, with QTY / free earned / to next free. Read-only.
import { Button, Card, Col, Empty, Input, Row, Table, Tag, Tooltip, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { PATHS } from '@routing/routes';
import { Roles } from 'common/constants';
import { AccountSelector } from '@features/Account';
import {
  LoyaltyCustomerItem,
  LoyaltyDashboardResponse,
  LoyaltyHighUnitCount,
  LoyaltyPageSize,
} from 'common/models/LoyaltyTypes';
import { OperationStatus } from 'store/rootTypes';
import { GetLoyaltyDashboard } from '../business/LoyaltyBusiness';
import { LoyaltySelector } from '../store/LoyaltySelector';
import { LoyaltyActions } from '../store/LoyaltySlice';
import { LoyaltyTheme } from './LoyaltyTheme';
import '../loyalty.css';

const MaxFilterLength = 256;

export const LoyaltyDashboard = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const accountState = useSelector(AccountSelector);
  const loyaltyState = useSelector(LoyaltySelector);

  const [emailInput, setEmailInput] = useState<string>(loyaltyState.AppliedEmail);

  const isAdmin = accountState.RoleName === Roles.Admin;

  // AC-25/26: strictly Admin (no allowlist). UX only - LoyaltyService.ShouldBeAdminUser is the control.
  useEffect(() => {
    if (!isAdmin) {
      history.push(PATHS.Home.path);
    }
  }, [accountState.RoleName]);

  // The query is committed only once the page has loaded: after a failed search the grid still
  // shows the previous results, so paging must keep using the filter those results came from.
  const fetchPage = async (appliedEmail: string, pageNumber: number) => {
    const data = (await dispatch(
      GetLoyaltyDashboard({ Email: appliedEmail, PageNumber: pageNumber, PageSize: LoyaltyPageSize })
    )) as unknown as LoyaltyDashboardResponse | undefined;
    if (data) {
      dispatch(LoyaltyActions.SetQuery({ AppliedEmail: appliedEmail, PageNumber: pageNumber }));
    }
  };

  // AC-13: all customers on load, no Search click needed. Coming Back from the detail page
  // keeps the applied filter and page.
  useEffect(() => {
    if (isAdmin) {
      fetchPage(loyaltyState.AppliedEmail, loyaltyState.PageNumber);
    }
  }, []);

  // AC-15/16: trimmed, back to page 1; blank shows everyone.
  const onSearch = () => fetchPage(emailInput.trim(), 1);

  // Paging always uses the APPLIED filter, never unsubmitted text in the box.
  const onPageChange = (page: number) => fetchPage(loyaltyState.AppliedEmail, page);

  // AC-19: key goes into the store, not the URL.
  const onNameClick = (record: LoyaltyCustomerItem) => {
    dispatch(LoyaltyActions.SetSelectedCustomerKey(record.CustomerKey));
    dispatch(LoyaltyActions.SetCustomerDetail(undefined));
    // A failed dashboard call must not read as a failed detail call on the next page.
    dispatch(LoyaltyActions.resetSubmitStatus());
    history.push(PATHS.LoyaltyCustomerDetail.path);
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'Name',
      key: 'Name',
      render: (value: string, record: LoyaltyCustomerItem) => (
        <Typography.Link onClick={() => onNameClick(record)}>{value}</Typography.Link>
      ),
    },
    { title: 'Email', dataIndex: 'Email', key: 'Email' },
    {
      title: 'QTY',
      dataIndex: 'Qty',
      key: 'Qty',
      align: 'right' as const,
      render: (value: number, record: LoyaltyCustomerItem) => (
        <>
          {value}
          {record.HasHighUnitCount && (
            <Tooltip title={`At least one invoice has more than ${LoyaltyHighUnitCount} units — check for a typo`}>
              <Tag className="loyalty-tag-invalid">Check units</Tag>
            </Tooltip>
          )}
        </>
      ),
    },
    { title: 'Free earned', dataIndex: 'FreeEarned', key: 'FreeEarned', align: 'right' as const },
    { title: 'To next free', dataIndex: 'ToNextFree', key: 'ToNextFree', align: 'right' as const },
  ];

  const dashboard = loyaltyState.Dashboard;
  const isLoading = loyaltyState.submitStatus === OperationStatus.pending;
  const hasNoResults = !!dashboard && dashboard.TotalCount === 0;

  return (
    <LoyaltyTheme>
      <div className="App">
        <div className="loyalty-page">
          <Row justify="center" align="middle">
            <h1>Loyalty 10 + 1</h1>
          </Row>

          <Card title="Customers received by KFS" className="card-custom loyalty-card">
            {/* 2026-09-28 US-010: filter box on its own row, then Back + Search together at the
                top (product owner), so neither needs a scroll past a long list. */}
            {/* 2026-09-28 US-010: visible label, not just a placeholder - the placeholder vanishes
                once typing starts and is too faint for some users; htmlFor ties it to the box. */}
            <label htmlFor="loyalty-email-filter" className="loyalty-filter-label">
              Filter by email
            </label>
            <Input
              id="loyalty-email-filter"
              size="large"
              placeholder="e.g. name@gmail.com"
              allowClear
              maxLength={MaxFilterLength}
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onPressEnter={onSearch}
            />
            <Row justify="end" gutter={8} className="loyalty-search">
              <Col>
                <Button size="large" onClick={() => history.push(PATHS.Home.path)}>
                  Back
                </Button>
              </Col>
              <Col>
                <Button type="primary" size="large" onClick={onSearch} loading={isLoading}>
                  Search
                </Button>
              </Col>
            </Row>

            {hasNoResults ? (
              // AC-17
              <Empty description="No customers found" />
            ) : (
              <Table
                dataSource={dashboard?.Items ?? []}
                columns={columns}
                rowKey="CustomerKey"
                size="middle"
                loading={isLoading}
                pagination={{
                  current: dashboard?.PageNumber ?? loyaltyState.PageNumber,
                  pageSize: LoyaltyPageSize,
                  total: dashboard?.TotalCount ?? 0,
                  showSizeChanger: false,
                  showTotal: (total: number) => `${total} customers`,
                  onChange: onPageChange,
                }}
              />
            )}
          </Card>
        </div>
      </div>
    </LoyaltyTheme>
  );
};
